'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { Button, Field, FieldError, H5, Input } from '@material-bank/mb3-components';
import { useShake } from '@material-bank/mb3-components/utils';
import { z, createFormSchema, useForm, SubmitHandler } from '@material-bank/mtag-forms';
import { Controller } from 'react-hook-form';
import { useTranslations } from '@material-bank/i18n';
const EMAIL_ERROR_ID = 'email-error';
function getEmailSchema(emailValidationMessage: string) {
  return z.object({
    email: z.string().email(emailValidationMessage)
  });
}
type EmailData = z.infer<ReturnType<typeof getEmailSchema>>;
type DialogEmailStepProps = {
  isLoading: boolean;
  error: string | null;
  onSubmit: (email: string) => void;
  // `intro` is the email field on the sheet's first screen, under its own title,
  // so it drops the heading, doesn't raise the keyboard on open and says Continue.
  variant?: 'step' | 'intro';
};
export function DialogEmailStep(t0) {
  const $ = _c(43);
  const {
    isLoading,
    error,
    onSubmit,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "step" : t1;
  const isIntro = variant === "intro";
  const t = useTranslations("authentication.dialog");
  let t2;
  let t3;
  let t4;
  if ($[0] !== t) {
    const emailSchema = getEmailSchema(t("emailValidation"));
    t4 = useForm;
    t2 = "onTouched";
    t3 = createFormSchema(emailSchema);
    $[0] = t;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t2 || $[5] !== t3) {
    t5 = {
      mode: t2,
      ...t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const form = t4(t5);
  const {
    ref: shakeRef,
    shakeIfMotionAllowed
  } = useShake();
  let t6;
  let t7;
  if ($[7] !== error || $[8] !== shakeIfMotionAllowed) {
    t6 = () => {
      if (!error) {
        return;
      }
      shakeIfMotionAllowed();
    };
    t7 = [error, shakeIfMotionAllowed];
    $[7] = error;
    $[8] = shakeIfMotionAllowed;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  if ($[11] !== onSubmit) {
    t8 = data => {
      onSubmit(data.email);
    };
    $[11] = onSubmit;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const submitHandler = t8;
  let t9;
  if ($[13] !== isIntro || $[14] !== t) {
    t9 = isIntro ? null : <H5>{t("emailHeading")}</H5>;
    $[13] = isIntro;
    $[14] = t;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== form || $[17] !== shakeIfMotionAllowed || $[18] !== submitHandler) {
    t10 = form.handleSubmit(submitHandler, shakeIfMotionAllowed);
    $[16] = form;
    $[17] = shakeIfMotionAllowed;
    $[18] = submitHandler;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = isIntro ? "flex flex-col gap-3" : "flex flex-1 flex-col gap-6";
  let t12;
  if ($[20] !== error || $[21] !== isIntro || $[22] !== isLoading || $[23] !== t) {
    t12 = t13 => {
      const {
        field,
        fieldState
      } = t13;
      const fieldError = error ? {
        message: error
      } : fieldState.error;
      const isInvalid = !!fieldError;
      return <Field><Input {...field} value={field.value ?? ""} id="email" type="email" placeholder={isIntro ? t("emailLabel") : t("emailPlaceholder")} aria-label={isIntro ? t("emailLabel") : undefined} disabled={isLoading} autoFocus={!isIntro} autoComplete="email" variant={isInvalid ? "destructive" : "default"} aria-invalid={isInvalid} aria-describedby={isInvalid ? EMAIL_ERROR_ID : undefined} /><FieldError id={EMAIL_ERROR_ID} errors={[fieldError]} /></Field>;
    };
    $[20] = error;
    $[21] = isIntro;
    $[22] = isLoading;
    $[23] = t;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== form.control || $[26] !== t12) {
    t13 = <Controller control={form.control} name="email" render={t12} />;
    $[25] = form.control;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== shakeRef || $[29] !== t13) {
    t14 = <div ref={shakeRef}>{t13}</div>;
    $[28] = shakeRef;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== isIntro || $[32] !== isLoading || $[33] !== t) {
    t15 = isIntro ? <Button type="submit" variant="brand" size="xl" disabled={isLoading} className="w-full rounded-full h-12" data-haptic="light" data-analyticsid="popup_modal_sign_in_sign_in_modal">{isLoading ? t("emailSubmitButtonLoading") : t("continue")}</Button> : <Button type="submit" variant="default" size="xl" disabled={isLoading} className="mt-auto w-full">{isLoading ? t("emailSubmitButtonLoading") : t("emailSubmitButton")}</Button>;
    $[31] = isIntro;
    $[32] = isLoading;
    $[33] = t;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t14 || $[38] !== t15) {
    t16 = <form noValidate={true} onSubmit={t10} className={t11}>{t14}{t15}</form>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t16 || $[41] !== t9) {
    t17 = <div className="flex h-full flex-1 flex-col gap-6 p-1">{t9}{t16}</div>;
    $[40] = t16;
    $[41] = t9;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  return t17;
}